import { useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { UserCheck } from 'lucide-react'
import { cn } from '@/lib/cn'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn } from '@/components/DataTable/types'
import { fetchReporterReliability, type AdminReporterRel } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'

/**
 * A7.04 — Shikoyatchi ishonchliligi. JONLI /admin/reports ni shikoyatchi bo'yicha
 * agregatlaydi. Dismiss-rate yuqori = ko'p ASOSSIZ shikoyat (ishonchsiz manba).
 * Alohida endpoint yo'q (klient-agregat).
 */
export function ReporterReliability() {
  const t = useT()

  const columns = useMemo<AdminColumn<AdminReporterRel>[]>(
    () => [
      {
        key: 'reporter_id',
        header: t.mod.reporter,
        type: 'text',
        minWidth: 180,
        sortable: false,
        render: (r) => (
          <Link to={`/users/${r.reporter_id}`} className="text-accent hover:underline">
            {r.username ? `@${r.username}` : `#${r.reporter_id}`}
          </Link>
        ),
      },
      {
        key: 'total',
        header: t.mod.reportCount,
        type: 'number',
        width: 110,
        align: 'right',
        render: (r) => <span className="font-mono tabular text-primary">{r.total}</span>,
      },
      { key: 'resolved', header: t.mod.resolved, type: 'number', width: 110, align: 'right' },
      { key: 'dismissed', header: t.mod.dismissed, type: 'number', width: 110, align: 'right' },
      {
        key: 'dismiss_rate',
        header: t.mod.dismissRate,
        type: 'number',
        width: 130,
        align: 'right',
        render: (r) => (
          <span className={cn('font-mono tabular', r.dismiss_rate >= 60 ? 'text-danger' : r.dismiss_rate >= 30 ? 'text-warning' : 'text-secondary')}>
            {r.dismiss_rate}%
          </span>
        ),
      },
    ],
    [t],
  )

  const fetcher = useCallback(fetchReporterReliability, [])

  return (
    <>
      <PageHeader
        title={t.mod.reporterReliability}
        screenId="A7.04"
        description={t.mod.reporterReliabilityNote}
        actions={
          <Link
            to="/moderation"
            className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
          >
            {t.mod.queue}
          </Link>
        }
      />
      <DataTable<AdminReporterRel>
        screenId="A7.04"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.reporter_id)}
        exportable
        emptyState={{ icon: <UserCheck size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
